import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  ProjectSummaryAction,
  ProjectSummaryActionContext,
  ProjectSummaryRow,
  ProjectSummarySort
} from '../../data-access/project-summary.types';

@Component({
  selector: 'app-project-summary-table',
  templateUrl: './project-summary-table.component.html',
  styleUrls: ['./project-summary-table.component.scss']
})
export class ProjectSummaryTableComponent {
  @Input() rows: ProjectSummaryRow[] = [];
  @Input() selectedProjectIds: number[] = [];
  @Input() indicateNewProjects = true;
  @Input() newSince?: string;
  @Input() updatedSince?: string;
  @Input() usage = 'default';
  @Input() sort?: ProjectSummarySort;
  @Input() actions: ProjectSummaryAction[] = [];

  @Output() rowSelected = new EventEmitter<ProjectSummaryRow>();
  @Output() selectionChanged = new EventEmitter<number[]>();
  @Output() restoreRequested = new EventEmitter<number>();
  @Output() sortChanged = new EventEmitter<ProjectSummarySort>();

  readonly displayedColumns: string[] = [
    'selected',
    'new',
    'projectName',
    'energySector',
    'country',
    'companyName',
    'dollarValue',
    'startupYear',
    'projectStage',
    'projectStatus',
    'trackingStatus',
    'projectUpdated'
  ];

  onRowClick(row: ProjectSummaryRow): void {
    if (row.excluded || row.isAccessible === false) {
      return;
    }

    this.rowSelected.emit(row);
  }

  isSelected(projectID: number): boolean {
    return this.selectedProjectIds.includes(projectID);
  }

  isAllSelected(): boolean {
    const selectable = this.rows.filter((item) => !item.excluded).map((item) => item.projectID);
    return selectable.length > 0 && selectable.every((id) => this.selectedProjectIds.includes(id));
  }

  toggleAll(checked: boolean): void {
    const nextIds = checked
      ? this.rows.filter((item) => !item.excluded).map((item) => item.projectID)
      : [];
    this.selectionChanged.emit(nextIds);
  }

  toggleProject(projectID: number, checked: boolean): void {
    const next = new Set<number>(this.selectedProjectIds);

    if (checked) {
      next.add(projectID);
    } else {
      next.delete(projectID);
    }

    this.selectionChanged.emit([...next]);
  }

  requestRestore(projectID: number): void {
    this.restoreRequested.emit(projectID);
  }

  requestSort(sortField: string): void {
    const isSameField = this.sort?.sortField === sortField;
    const sortDesc = isSameField ? !this.sort?.sortDesc : false;
    this.sortChanged.emit({ sortField, sortDesc: !!sortDesc });
  }

  projectIsNew(row: ProjectSummaryRow): boolean {
    if (!this.indicateNewProjects || !this.newSince || !row.created) {
      return false;
    }

    return new Date(row.created).getTime() >= new Date(this.newSince).getTime();
  }

  projectUpdatedAfterHeadingDate(row: ProjectSummaryRow): boolean {
    if (!this.updatedSince || !row.projectUpdated) {
      return false;
    }

    return new Date(row.projectUpdated).getTime() >= new Date(this.updatedSince).getTime();
  }

  sortIcon(sortField: string): string {
    return this.sort?.sortField === sortField
      ? this.sort.sortDesc
        ? 'arrow_circle_up'
        : 'arrow_circle_down'
      : '';
  }

  isSorted(sortField: string): boolean {
    return this.sort?.sortField === sortField;
  }

  actionContext(): ProjectSummaryActionContext {
    const selectableCount = this.rows.filter((item) => !item.excluded).length;

    return {
      usage: this.usage,
      selectedProjectIds: this.selectedProjectIds,
      selectedCount: this.selectedProjectIds.length,
      selectableCount,
      allSelectableSelected: selectableCount > 0 && this.selectedProjectIds.length === selectableCount
    };
  }

  actionVisible(action: ProjectSummaryAction): boolean {
    return action.visible ? action.visible(this.actionContext()) : true;
  }

  actionDisabled(action: ProjectSummaryAction): boolean {
    return action.disabled ? action.disabled(this.actionContext()) : false;
  }

  runAction(action: ProjectSummaryAction, event?: Event): void {
    event?.stopPropagation();

    try {
      action.onClick(this.actionContext());
    } catch (error) {
      console.error(`[project-summary] action failed: ${action.id}`, error);
    }
  }
}
